import React, { useState, useEffect } from 'react';
import {
    Building,
    Printer,
    CreditCard,
    User,
    Globe,
    Bell,
    Save,
    Moon,
    Sun
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { cn } from '../../utils/theme';

export default function Settings() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('general');
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('theme') === 'dark' ||
            document.documentElement.classList.contains('dark');
    });

    const [restaurantName, setRestaurantName] = useState(
        localStorage.getItem('restaurant_name') || 'Mi Restaurante'
    );

    const [printerIp, setPrinterIp] = useState(
        localStorage.getItem('printer_ip') || ''
    );

    const toggleTheme = () => {
        const isDark = !darkMode;
        setDarkMode(isDark);
        if (isDark) {
            document.documentElement.classList.add('dark');
            localStorage.setItem('theme', 'dark');
        } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('theme', 'light');
        }
    };

    const handleSaveGeneral = () => {
        localStorage.setItem('restaurant_name', restaurantName);
        localStorage.setItem('printer_ip', printerIp);
        // Here you would typically save to backend
        alert('Configuración guardada localmente');
    };

    const tabs = [
        { id: 'general', label: 'General', icon: Building },
        { id: 'hardware', label: 'Hardware e Impresión', icon: Printer },
        { id: 'payments', label: 'Pagos', icon: CreditCard },
        { id: 'account', label: 'Cuenta', icon: User },
    ];

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Configuración</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">Administra las preferencias de tu sistema</p>
            </div>

            <div className="flex flex-col md:flex-row gap-6">
                {/* Sidebar Tabs */}
                <div className="w-full md:w-64 flex flex-col gap-2">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={cn(
                                    "flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors font-medium",
                                    activeTab === tab.id
                                        ? "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400"
                                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-neutral-800"
                                )}
                            >
                                <Icon size={20} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Content Area */}
                <div className="flex-1 bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-100 dark:border-neutral-700 p-6 min-h-[500px]">

                    {/* GENERAL TAB */}
                    {activeTab === 'general' && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-xl font-semibold mb-4">Información del Negocio</h2>
                                <div className="grid gap-4 max-w-lg">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Nombre del Restaurante
                                        </label>
                                        <input
                                            type="text"
                                            value={restaurantName}
                                            onChange={(e) => setRestaurantName(e.target.value)}
                                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-neutral-700/50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            {darkMode ? <Moon size={20} className="text-purple-400" /> : <Sun size={20} className="text-amber-500" />}
                                            <div>
                                                <p className="font-medium">Modo Oscuro</p>
                                                <p className="text-xs text-gray-500">Cambia la apariencia de la aplicación</p>
                                            </div>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input type="checkbox" checked={darkMode} onChange={toggleTheme} className="sr-only peer" />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-gray-100 dark:border-neutral-700">
                                <button
                                    onClick={handleSaveGeneral}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                                >
                                    <Save size={18} /> Guardar Cambios
                                </button>
                            </div>
                        </div>
                    )}

                    {/* HARDWARE TAB */}
                    {activeTab === 'hardware' && (
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold mb-4">Configuración de Hardware</h2>

                            <div className="p-4 border border-blue-100 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 rounded-lg text-blue-800 dark:text-blue-300">
                                <div className="flex gap-3">
                                    <Printer size={24} />
                                    <div>
                                        <h3 className="font-semibold">Impresoras Térmicas</h3>
                                        <p className="text-sm mt-1">Configura la impresión de recibos y comandas.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="max-w-lg">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    IP de Impresora Principal
                                </label>
                                <input
                                    type="text"
                                    placeholder="192.168.1.100"
                                    value={printerIp}
                                    onChange={(e) => setPrinterIp(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <p className="text-xs text-gray-500 mt-1">Dejar vacío si usa impresión nativa del navegador.</p>
                            </div>
                            <div className="pt-4 border-t border-gray-100 dark:border-neutral-700">
                                <button
                                    onClick={handleSaveGeneral}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                                >
                                    <Save size={18} /> Guardar Configuración
                                </button>
                            </div>
                        </div>
                    )}

                    {/* PAYMENTS TAB */}
                    {activeTab === 'payments' && (
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold mb-4">Métodos de Pago</h2>
                            <p className="text-gray-500">Gestiona los métodos de pago aceptados en tu negocio.</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <button
                                    onClick={() => navigate('/cash/methods')}
                                    className="p-4 border rounded-xl hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all text-left group"
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg group-hover:bg-blue-100 group-hover:text-blue-600">
                                            <CreditCard size={24} />
                                        </div>
                                        <span className="font-semibold">Configurar Métodos</span>
                                    </div>
                                    <p className="text-sm text-gray-500">Agregar o quitar métodos de pago (Efectivo, Tarjeta, Transferencia).</p>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ACCOUNT TAB */}
                    {activeTab === 'account' && (
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold mb-4">Mi Cuenta</h2>
                            <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-neutral-700/50 rounded-xl">
                                <div className="p-3 bg-blue-100 text-blue-600 rounded-full">
                                    <User size={32} />
                                </div>
                                <div>
                                    <h3 className="font-semibold">Perfil de Usuario</h3>
                                    <p className="text-sm text-gray-500">Gestiona tu información personal y contraseña.</p>
                                </div>
                                <button
                                    onClick={() => navigate('/profile')}
                                    className="ml-auto px-4 py-2 text-sm bg-white border border-gray-200 shadow-sm rounded-lg hover:bg-gray-50 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-600"
                                >
                                    Ir al Perfil
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
